import { useMemo } from "react";
import { FileText, Mail, MessageCircle, Phone } from "lucide-react";
import { PreOrderSourceType, type PreOrder } from "@/app/api/types/preOrder";
import type { WhatsAppChatResponse } from "@/app/api/types/whatsapp";
import type { SMSChatResponse } from "@/app/api/types/sms";
import {
  collapseChatIdsToViewerSeats,
  resolveViewerSeat,
} from "@/app/chat/lib/viewerSeat";
import type {
  LinkedSourceEntry,
  LinkedSourceChannel,
} from "../components/LinkedSourcesPopover";
import type { SourceIconChannel } from "../components/SourceIconCluster";

/**
 * Origin-only channels: surfaced as cluster icons when a pre-order has a
 * non-message origin (manual / internal_offer) but never as openable popover
 * rows — there is no conversation to link to.
 */
type OriginIconChannel = "manual" | "internal_offer";
type IconChannelKey = LinkedSourceChannel | OriginIconChannel;

const CHANNEL_VISUAL: Record<
  IconChannelKey,
  { color: string; hover: string; icon: SourceIconChannel["Icon"] }
> = {
  email: {
    color: "text-blue-600",
    hover: "hover:bg-blue-50",
    icon: Mail,
  },
  whatsapp: {
    color: "text-green-600",
    hover: "hover:bg-green-50",
    icon: MessageCircle,
  },
  sms: {
    color: "text-purple-600",
    hover: "hover:bg-purple-50",
    icon: Phone,
  },
  manual: {
    color: "text-gray-400",
    hover: "hover:bg-gray-50",
    icon: FileText,
  },
  internal_offer: {
    color: "text-amber-600",
    hover: "hover:bg-amber-50",
    icon: FileText,
  },
};

export interface LinkedSourcesResult {
  entries: LinkedSourceEntry[];
  /**
   * Distinct channels present in cluster order: origin channel first
   * (email/whatsapp/sms/manual/internal_offer), then any remaining linked
   * message channels in email → whatsapp → sms order, deduped.
   */
  channels: SourceIconChannel[];
  /** Total number of linked items across all channels. */
  total: number;
  /** Hover class for the trigger — single-channel uses that channel's hover. */
  triggerHoverClass: string;
  /** Default entry to open when the trigger is clicked (matches source_type). */
  defaultEntry: LinkedSourceEntry | null;
}

/**
 * Pure resolver for sources linked to a pre-order across email / WhatsApp / SMS.
 *
 * Includes legacy single-id fields (`email_id`, `whatsapp_chat_id`,
 * `sms_chat_id`) and the junction-based `linked_*_chat_ids` / `linked_email_ids`
 * arrays. Names for non-primary chats fall back to a generic label since a
 * per-row global chat lookup would subscribe every header to the chat list —
 * popovers can enrich names on demand via `useWhatsAppChats` / `useSMSChats`
 * when opened.
 */
export interface UseLinkedSourcesOptions {
  /**
   * The viewing user's id. When provided, sibling-email copies that share a
   * Message-Id collapse to a single entry, preferring the one owned by this
   * user. Optional so legacy callers / tests don't break — without it
   * collapse is skipped and every linked email surfaces as its own entry.
   */
  currentUserId?: string;
  /**
   * Chat ids the viewer can actually open — their visibility-filtered Dexie
   * whatsapp / sms chat lists (owner + group). When provided, WhatsApp/SMS
   * linked chats absent from the set are dropped so the source affordance
   * never offers a conversation the viewer can't load (empty preview).
   * Omitted by legacy callers / tests → no gating.
   */
  visibleWhatsappChatIds?: ReadonlySet<string>;
  visibleSmsChatIds?: ReadonlySet<string>;
  /**
   * Viewer's full WhatsApp / SMS chat rows (legacy snake_case shape, the
   * output of `adaptOfflineWhatsappChat` / `adaptOfflineSmsChat`). Used to
   * collapse sibling seats of a shared-group conversation to the viewer's
   * own row before building popover entries — so the operator sees one row
   * per external conversation and the default click opens the seat whose
   * instance they actually own (outbound goes through their WhatsApp).
   * Omitted by legacy callers / tests → collapse is skipped and the legacy
   * one-entry-per-seat behaviour is preserved.
   */
  viewerWhatsappChats?: readonly WhatsAppChatResponse[];
  viewerSmsChats?: readonly SMSChatResponse[];
}

export function useLinkedSources(
  preOrder: PreOrder,
  options: UseLinkedSourcesOptions = {},
): LinkedSourcesResult {
  const {
    currentUserId,
    visibleWhatsappChatIds,
    visibleSmsChatIds,
    viewerWhatsappChats,
    viewerSmsChats,
  } = options;
  // Destructure into individual fields so the memo depends on those, not the
  // PreOrder object identity. A future `{ ...po }` reconstruction in either
  // data source (offlineShim.mapOfflinePreOrder / adaptV2PreOrderTyped) copies
  // the same field references, so a value-unchanged PO still hits the memo.
  const { email_id, source_type, whatsapp_chat_id, sms_chat_id } = preOrder;
  const emails = preOrder.emails;
  const linkedEmails = preOrder.linked_emails;
  const linkedEmailIds = preOrder.linked_email_ids;
  const contactEmail = preOrder.email_contact?.email_address;
  const linkedWhatsappChatIds = preOrder.linked_whatsapp_chat_ids;
  const whatsappName =
    preOrder.whatsapp_chat?.display_name ?? preOrder.whatsapp_chat?.name;
  const linkedSmsChatIds = preOrder.linked_sms_chat_ids;
  const smsName =
    preOrder.sms_chat?.display_name ?? preOrder.sms_chat?.phone_number;

  return useMemo(() => {
    const entries: LinkedSourceEntry[] = [
      ...buildEmailEntries(
        emails,
        linkedEmails,
        email_id,
        linkedEmailIds,
        contactEmail,
        currentUserId,
      ),
      ...buildChatEntries(
        "whatsapp",
        collapseSiblingSeats(
          gateByVisibleChats(
            unionIds(linkedWhatsappChatIds, whatsapp_chat_id),
            visibleWhatsappChatIds,
          ),
          viewerWhatsappChats,
          (c) => c.jid,
          currentUserId,
        ),
        // Swap the primary chat id through the same viewer-seat rewrite so
        // `isPrimary` still matches against the collapsed entry — otherwise
        // the popover would lose the primary name + ordering whenever the
        // trigger user's seat collapsed to the viewer's seat.
        resolvePrimarySeat(
          whatsapp_chat_id,
          viewerWhatsappChats,
          (c) => c.jid,
          currentUserId,
        ),
        whatsappName,
        "WhatsApp chat",
      ),
      ...buildChatEntries(
        "sms",
        collapseSiblingSeats(
          gateByVisibleChats(
            unionIds(linkedSmsChatIds, sms_chat_id),
            visibleSmsChatIds,
          ),
          viewerSmsChats,
          (c) => c.phone_number,
          currentUserId,
        ),
        resolvePrimarySeat(
          sms_chat_id,
          viewerSmsChats,
          (c) => c.phone_number,
          currentUserId,
        ),
        smsName,
        "SMS chat",
      ),
    ];

    // Always surface the pre-order's origin channel first so manual /
    // internal_offer creations keep their icon even after external messages
    // get linked. Without this, a manual order followed by one WhatsApp
    // update shows only the manual icon (and the click opens the chat —
    // misleading), and the same order after a second WhatsApp link loses
    // the manual icon entirely. Message-origin pre-orders (email/whatsapp/
    // sms) get their origin channel through the entry pipeline already; the
    // include-once guard below keeps the cluster from doubling up.
    const distinctChannels: IconChannelKey[] = [];
    const originChannel = sourceTypeToIconChannel(source_type);
    if (originChannel) distinctChannels.push(originChannel);
    for (const ch of ["email", "whatsapp", "sms"] as const) {
      if (
        entries.some((e) => e.channel === ch) &&
        !distinctChannels.includes(ch)
      ) {
        distinctChannels.push(ch);
      }
    }

    const channels: SourceIconChannel[] = distinctChannels.map((ch) => ({
      key: ch,
      Icon: CHANNEL_VISUAL[ch].icon,
      colorClass: CHANNEL_VISUAL[ch].color,
    }));

    const triggerHoverClass =
      distinctChannels.length === 1
        ? CHANNEL_VISUAL[distinctChannels[0]!].hover
        : "hover:bg-gray-50";

    return {
      entries,
      channels,
      total: entries.length,
      triggerHoverClass,
      defaultEntry: resolveDefaultEntry(
        entries,
        sourceTypeToChannel(source_type),
      ),
    };
  }, [
    emails,
    linkedEmails,
    email_id,
    linkedEmailIds,
    contactEmail,
    currentUserId,
    source_type,
    linkedWhatsappChatIds,
    whatsapp_chat_id,
    whatsappName,
    linkedSmsChatIds,
    sms_chat_id,
    smsName,
    visibleWhatsappChatIds,
    visibleSmsChatIds,
    viewerWhatsappChats,
    viewerSmsChats,
  ]);
}

/**
 * Collapse a list of linked chat ids to one id per external conversation,
 * preferring the viewer's seat. Falls back to the legacy id list when no
 * viewer chat catalogue is supplied (test / legacy callers) so the popover
 * keeps its pre-fix behaviour in those callers.
 */
function collapseSiblingSeats<T extends { id: string }>(
  ids: string[],
  viewerChats: readonly T[] | undefined,
  conversationKey: (chat: T) => string | null | undefined,
  viewerUserId: string | undefined,
): string[] {
  if (!viewerChats) return ids;
  return collapseChatIdsToViewerSeats(
    ids,
    viewerChats,
    conversationKey,
    viewerUserId,
  );
}

/**
 * Resolve the primary chat id through the same seat rewrite the linked-list
 * collapse uses, so `buildChatEntries.isPrimary` still matches after the
 * trigger user's seat is swapped to the viewer's. Legacy callers that don't
 * supply a viewer catalogue get the unmodified primary back.
 */
function resolvePrimarySeat<T extends { id: string }>(
  primary: string | undefined,
  viewerChats: readonly T[] | undefined,
  conversationKey: (chat: T) => string | null | undefined,
  viewerUserId: string | undefined,
): string | undefined {
  if (!primary || !viewerChats) return primary;
  return resolveViewerSeat(primary, viewerChats, conversationKey, viewerUserId);
}

/**
 * Email entries. The offline shim never populates the rich `emails[]` array
 * (it sets it to `[]` and exposes only `linked_email_ids`); the legacy recap
 * path fills `emails[]`. Union both so the badge sees every linked email
 * regardless of transport — rich metadata when available, generic fallback
 * otherwise (mirrors `buildChatEntries`).
 */
type EmailMeta = { user_id?: string; message_id?: string };
type RichEmail = NonNullable<PreOrder["emails"]>[number];

function buildEmailEntries(
  emails: PreOrder["emails"],
  linkedEmails: PreOrder["linked_emails"],
  emailId: PreOrder["email_id"],
  linkedEmailIds: PreOrder["linked_email_ids"],
  contactEmail: string | undefined,
  currentUserId: string | undefined,
): LinkedSourceEntry[] {
  const richEmailById = new Map((emails ?? []).map((e) => [e.id, e]));
  const metaById = buildMetaById(linkedEmails);
  const allIds = collectEmailIds(emails, linkedEmails, emailId, linkedEmailIds);
  const groups = groupSiblings(allIds, metaById, currentUserId);

  const out: LinkedSourceEntry[] = [];
  for (const ids of groups.values()) {
    const chosenId = pickChosenId(ids, metaById, currentUserId);
    out.push(buildEntry(chosenId, richEmailById, emailId, contactEmail));
  }
  return out;
}

/**
 * Per-id metadata from `linked_emails` (offline shim) — drives sibling
 * collapse and is also the visibility signal: an id without meta points
 * at an email the viewer can't read.
 */
function buildMetaById(
  linkedEmails: PreOrder["linked_emails"],
): Map<string, EmailMeta> {
  const m = new Map<string, EmailMeta>();
  for (const e of linkedEmails ?? []) {
    m.set(e.id, { user_id: e.user_id, message_id: e.message_id });
  }
  return m;
}

/**
 * When the offline shim has resolved a visibility set (`linked_emails[]`)
 * or the legacy path has rich entries (`emails[]`), trust that as the
 * authoritative list of emails this viewer can open. Bare `email_id` /
 * `linked_email_ids` are then ignored — they may reference dedup-sibling
 * copies in other users' mailboxes that the API's email visibility filter
 * never sent us, and surfacing those as non-clickable stubs is worse than
 * hiding them. Without either signal (manual PO with only a forward FK),
 * we fall back to the union behaviour so legacy data still renders.
 */
function collectEmailIds(
  emails: PreOrder["emails"],
  linkedEmails: PreOrder["linked_emails"],
  emailId: PreOrder["email_id"],
  linkedEmailIds: PreOrder["linked_email_ids"],
): Set<string> {
  const hasVisibilityData =
    (linkedEmails?.length ?? 0) > 0 || (emails?.length ?? 0) > 0;
  const allIds = new Set<string>();
  for (const e of emails ?? []) if (e.id) allIds.add(e.id);
  for (const e of linkedEmails ?? []) if (e.id) allIds.add(e.id);
  if (!hasVisibilityData) {
    if (emailId) allIds.add(emailId);
    for (const id of linkedEmailIds ?? []) if (id) allIds.add(id);
  }
  return allIds;
}

/**
 * Group by Message-Id when known AND a viewer is provided — collapse is
 * the per-viewer behaviour the new option opts into. Legacy callers
 * (no `currentUserId`) keep every linked email as its own entry to
 * preserve pre-branch behaviour. Entries without a message_id are also
 * solo (`__solo:${id}`) so unmeta'd rows still surface individually.
 */
function groupSiblings(
  allIds: Set<string>,
  metaById: Map<string, EmailMeta>,
  currentUserId: string | undefined,
): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const id of allIds) {
    const mid = metaById.get(id)?.message_id;
    const key = currentUserId && mid ? `mid:${mid}` : `__solo:${id}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(id);
    else groups.set(key, [id]);
  }
  return groups;
}

/**
 * Inside a group, prefer the entry owned by the current user when we can
 * identify it; otherwise pick a sibling with known meta so the click
 * target is a stable, real email rather than an unsynced stub. Whether
 * the resolved email is actually readable is decided by useEmail — Dexie's
 * visibility filter already gates content access.
 */
function pickChosenId(
  ids: string[],
  metaById: Map<string, EmailMeta>,
  currentUserId: string | undefined,
): string {
  if (!currentUserId) return ids[0];
  const mine = ids.find((id) => metaById.get(id)?.user_id === currentUserId);
  if (mine) return mine;
  return ids.find((id) => metaById.get(id)?.user_id !== undefined) ?? ids[0];
}

function buildEntry(
  chosenId: string,
  richEmailById: Map<string, RichEmail>,
  emailId: PreOrder["email_id"],
  contactEmail: string | undefined,
): LinkedSourceEntry {
  const rich = richEmailById.get(chosenId);
  const isPrimary = emailId === chosenId;
  const primaryLabel =
    rich?.from_address ??
    (isPrimary ? contactEmail : undefined) ??
    "Email message";
  return {
    channel: "email",
    chatId: chosenId,
    primary: primaryLabel,
    secondary: rich?.email_subject || undefined,
    date: rich?.email_date,
    isPrimary,
  };
}

/**
 * WhatsApp / SMS entries — one per linked chat id. Only the primary chat
 * carries a resolved display name; non-primary chats fall back to a generic
 * label (a per-row global chat lookup would subscribe every header to the
 * chat list — popovers enrich names on demand instead).
 */
function buildChatEntries(
  channel: "whatsapp" | "sms",
  chatIds: string[],
  primaryChatId: string | undefined,
  primaryName: string | undefined,
  defaultLabel: string,
): LinkedSourceEntry[] {
  return chatIds.map((chatId) => {
    const isPrimary = primaryChatId === chatId;
    return {
      channel,
      chatId,
      primary: (isPrimary && primaryName) || defaultLabel,
      isPrimary,
    };
  });
}

/**
 * The entry opened when the source icon is clicked. Prefers the primary entry
 * matching the PO's `source_type`, then any primary, then the first entry.
 * When the result is an email and the PO links several emails, the most
 * recent one wins — ISO `date` strings compare lexicographically; entries
 * with no date sort as oldest.
 */
function resolveDefaultEntry(
  entries: LinkedSourceEntry[],
  sourceChannel: LinkedSourceChannel | undefined,
): LinkedSourceEntry | null {
  const defaultEntry =
    entries.find(
      (e) => e.isPrimary && (!sourceChannel || e.channel === sourceChannel),
    ) ??
    entries.find((e) => e.isPrimary) ??
    entries[0] ??
    null;

  if (defaultEntry?.channel === "email") {
    const emailEntries = entries.filter((e) => e.channel === "email");
    if (emailEntries.length > 1) {
      return emailEntries.reduce(
        (newest, e) => ((e.date ?? "") > (newest.date ?? "") ? e : newest),
        emailEntries[0]!,
      );
    }
  }
  return defaultEntry;
}

/**
 * Drop chat ids the viewer can't open. `visible` is the viewer's Dexie
 * chat-list id set (owner + group visibility); when undefined (legacy
 * callers / tests) or empty (SyncEngine cold-pull not yet complete) no
 * gating is applied so behaviour is unchanged. An empty set is treated as
 * "not loaded" because a user who has WhatsApp/SMS pre-orders necessarily
 * owns at least one chat — an empty set means the offline store hasn't
 * hydrated yet, not that the viewer genuinely has zero chats.
 */
function gateByVisibleChats(
  ids: string[],
  visible: ReadonlySet<string> | undefined,
): string[] {
  return visible?.size ? ids.filter((id) => visible.has(id)) : ids;
}

function unionIds(
  list: readonly string[] | undefined,
  primary: string | undefined,
): string[] {
  const set = new Set<string>();
  if (primary) set.add(primary);
  if (list) for (const id of list) if (id) set.add(id);
  return Array.from(set);
}

function sourceTypeToChannel(
  sourceType: number,
): LinkedSourceChannel | undefined {
  switch (sourceType) {
    case PreOrderSourceType.EMAIL:
      return "email";
    case PreOrderSourceType.WHATSAPP:
      return "whatsapp";
    case PreOrderSourceType.SMS:
      return "sms";
    default:
      return undefined;
  }
}

/**
 * Maps a PreOrder.source_type to its cluster-icon channel key. Unlike
 * {@link sourceTypeToChannel}, this also returns synthetic origin keys
 * ("manual" / "internal_offer") that have no openable entry but should
 * still surface as a cluster icon.
 */
function sourceTypeToIconChannel(
  sourceType: number,
): IconChannelKey | undefined {
  switch (sourceType) {
    case PreOrderSourceType.EMAIL:
      return "email";
    case PreOrderSourceType.WHATSAPP:
      return "whatsapp";
    case PreOrderSourceType.SMS:
      return "sms";
    case PreOrderSourceType.MANUAL:
      return "manual";
    case PreOrderSourceType.INTERNAL_OFFER:
      return "internal_offer";
    default:
      return undefined;
  }
}
